import { supabase } from '@/integrations/supabase/client';
import type { Harvest } from '@/types/database.types';

export const getHarvests = async () => {
  console.log("HarvestService: Fetching all harvests");
  try {
    const { data, error } = await supabase
      .from('harvests')
      .select('*, farms(product_name, farmer_name)')
      .order('expected_date', { ascending: true });
    
    if (error) {
      console.error("HarvestService: Error fetching harvests:", error);
      
      // Attempt fallback query without the join
      console.log("HarvestService: Attempting fallback query without join");
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('harvests')
        .select('*')
        .order('expected_date', { ascending: true });
        
      if (fallbackError) {
        console.error("HarvestService: Fallback query also failed:", fallbackError);
        throw fallbackError;
      }
      
      console.log(`HarvestService: Retrieved ${fallbackData.length} harvest records (without farm data)`);
      return fallbackData as Harvest[];
    }
    
    // Map the farms data to the main harvest objects
    const processedData = data.map(harvest => ({
      ...harvest,
      farm_product_name: harvest.farms?.product_name || null,
      farm_farmer_name: harvest.farms?.farmer_name || null,
    }));
    
    console.log(`HarvestService: Retrieved ${processedData.length} harvest records`);
    return processedData as Harvest[];
  } catch (error) {
    console.error("HarvestService: Unexpected error when fetching harvests:", error);
    throw error;
  }
};

export const getHarvestById = async (id: string) => {
  console.log(`HarvestService: Fetching harvest with ID: ${id}`);
  
  try {
    const { data, error } = await supabase
      .from('harvests')
      .select('*, farms(product_name, farmer_name)')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error(`HarvestService: Error fetching harvest ${id}:`, error);
      
      // Attempt fallback query without the join
      console.log(`HarvestService: Attempting fallback query for harvest ${id} without join`);
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('harvests')
        .select('*')
        .eq('id', id)
        .maybeSingle();
        
      if (fallbackError) {
        console.error(`HarvestService: Fallback query for harvest ${id} also failed:`, fallbackError);
        throw fallbackError;
      }
      
      if (!fallbackData) {
        console.log(`HarvestService: No harvest found with ID ${id}`);
        throw new Error(`Harvest with ID ${id} not found`);
      }
      
      return fallbackData as Harvest;
    }
    
    const processedData = {
      ...data,
      farm_product_name: data.farms?.product_name || null,
      farm_farmer_name: data.farms?.farmer_name || null,
    };
    
    console.log(`HarvestService: Retrieved harvest ${id}`);
    return processedData as Harvest;
  } catch (error) {
    console.error(`HarvestService: Unexpected error when fetching harvest ${id}:`, error);
    throw error;
  }
};

export const getHarvestsByFarmId = async (farmId: string) => {
  if (!farmId) {
    console.log("HarvestService: No farm ID provided, returning empty array");
    return [];
  }
  
  console.log(`HarvestService: Fetching harvests for farm ID: ${farmId}`);
  
  try {
    const { data, error } = await supabase
      .from('harvests')
      .select('*, farms(product_name, farmer_name)')
      .eq('farm_id', farmId)
      .order('expected_date', { ascending: true });
    
    if (error) {
      console.error(`HarvestService: Error fetching harvests for farm ${farmId}:`, error);
      
      // Attempt fallback query without the join
      console.log(`HarvestService: Attempting fallback query for farm ${farmId} without join`);
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('harvests')
        .select('*')
        .eq('farm_id', farmId)
        .order('expected_date', { ascending: true });
        
      if (fallbackError) {
        console.error(`HarvestService: Fallback query for farm ${farmId} also failed:`, fallbackError);
        throw fallbackError;
      }
      
      console.log(`HarvestService: Retrieved ${fallbackData.length} harvest records for farm ${farmId} (without farm data)`);
      return fallbackData as Harvest[];
    }
    
    // Map the farms data to the main harvest objects
    const processedData = data.map(harvest => ({
      ...harvest,
      farm_product_name: harvest.farms?.product_name || null,
      farm_farmer_name: harvest.farms?.farmer_name || null,
    }));
    
    console.log(`HarvestService: Retrieved ${processedData.length} harvest records for farm ${farmId}`);
    return processedData as Harvest[];
  } catch (error) {
    console.error(`HarvestService: Unexpected error when fetching harvests for farm ${farmId}:`, error);
    throw error;
  }
};

export const createHarvest = async (harvest: Omit<Harvest, 'id' | 'created_at' | 'updated_at'>) => {
  console.log("HarvestService: Creating new harvest", harvest);
  
  try {
    const { data, error } = await supabase
      .from('harvests')
      .insert(harvest)
      .select();
    
    if (error) {
      console.error("HarvestService: Error creating harvest:", error);
      throw error;
    }
    
    console.log("HarvestService: Successfully created harvest with ID:", data[0].id);
    return data[0] as Harvest;
  } catch (error) {
    console.error("HarvestService: Unexpected error when creating harvest:", error);
    throw error;
  }
};

export const updateHarvest = async (id: string, harvest: Partial<Omit<Harvest, 'id' | 'created_at' | 'updated_at'>>) => {
  console.log(`HarvestService: Updating harvest ${id}`, harvest);
  
  try {
    const { data, error } = await supabase
      .from('harvests')
      .update(harvest)
      .eq('id', id)
      .select();
    
    if (error) {
      console.error(`HarvestService: Error updating harvest ${id}:`, error);
      throw error;
    }
    
    console.log(`HarvestService: Successfully updated harvest ${id}`);
    return data[0] as Harvest;
  } catch (error) {
    console.error(`HarvestService: Unexpected error when updating harvest ${id}:`, error);
    throw error;
  }
};

export const deleteHarvest = async (id: string) => {
  console.log(`HarvestService: Deleting harvest ${id}`);
  
  try {
    const { error } = await supabase
      .from('harvests')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error(`HarvestService: Error deleting harvest ${id}:`, error);
      throw error;
    }
    
    console.log(`HarvestService: Successfully deleted harvest ${id}`);
    return true;
  } catch (error) {
    console.error(`HarvestService: Unexpected error when deleting harvest ${id}:`, error);
    throw error;
  }
};
